/**
 * NeedsAttention - Alert section for critical portfolio positions.
 *
 * Surfaces positions where:
 * - Stance is BEARISH or LEANING_BEARISH AND total_gain_loss < 0
 * - OR bear_risk > 75 regardless of P&L
 */

import { AlertTriangle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StanceGauge } from './StanceGauge';
import type { PositionWithScan } from '@/types/portfolio';

interface NeedsAttentionProps {
  positions: PositionWithScan[];
  onDeepDive: (ticker: string) => void;
  className?: string;
}

function formatPct(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function NeedsAttention({ positions, onDeepDive, className }: NeedsAttentionProps) {
  const flagged = positions.filter((p) => p.needsAttention);

  if (flagged.length === 0) return null;

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-red-400" />
        <h3 className="text-sm font-semibold text-red-400">
          Needs Attention ({flagged.length})
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {flagged.map((p) => (
          <button
            key={p.symbol}
            onClick={() => onDeepDive(p.symbol)}
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg text-left',
              'bg-red-500/5 border border-red-500/20',
              'hover:border-red-500/40 transition-colors',
            )}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-text-primary">{p.symbol}</span>
                <span className={cn(
                  'text-xs font-medium tabular-nums',
                  p.total_gain_loss >= 0 ? 'text-green-400' : 'text-red-400',
                )}>
                  {formatPct(p.total_gain_loss_pct)}
                </span>
                <span className="text-xs text-text-muted tabular-nums">
                  {formatCurrency(p.total_gain_loss)}
                </span>
              </div>
              {p.scan && (
                <StanceGauge
                  bullIntensity={p.scan.bull_intensity}
                  bearRisk={p.scan.bear_risk}
                  stance={p.scan.stance}
                  size="sm"
                />
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-text-muted shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
